import Alpine from '@alpinejs/csp';
import AsyncAlpine from 'async-alpine';
import collapse from '@alpinejs/collapse';
import intersect from '@alpinejs/intersect';
import focus from '@alpinejs/focus';
import { createRouter } from './router/swup.js';
import { initScrollReveals } from './animations/scroll-reveal.js';
import { initHomeIntro, scaleHomeIntro } from './animations/home-intro.js';
import { initTagline } from './animations/tagline.js';
import { initHeroReveal } from './animations/hero.js';
import { initDirectorsReveal } from './animations/directors-reveal.js';
import { initNavActiveSync } from './animations/nav-active-sync.js';
import { initPageviewTracking } from './analytics/virtual-pageview.js';
import { initEventTracking } from './analytics/events.js';

// Active Level 2 component imports
import accordion from './components/accordion.js';
import melbourneClock from './components/melbourne-clock.js';
import testimonialsCarousel from './components/testimonials-carousel.js';
import toastContainer from './components/toast-container.js';
import pong404 from './components/pong-404.js';
import projectGalleryCarousel from './components/project-gallery-carousel.js';
import projectStoryDrawer from './components/project-story-drawer.js';
import projectScrollHint from './components/project-scroll-hint.js';
import stickyHeader from './components/sticky-header.js';
import directorsPin from './components/directors-pin.js';
import windFieldControls from './components/wind-field-controls.js';
import logoPongTrigger from './components/logo-pong-trigger.js';
import backLink from './components/back-link.js';
import fitText from './components/fit-text.js';

/*
 * PSD-Stack — App Bootstrap
 *
 * Initialisation order matters:
 * 1. Alpine plugins (must register before Alpine.start())
 * 2. Alpine stores (must register before Alpine.start())
 * 3. Alpine.data() component registrations
 * 4. Async Alpine (must init before Alpine.start())
 * 5. Alpine.start()
 * 6. Swup router (after DOM is ready)
 * 7. Analytics (pageview tracking + structural events + deferred GTM)
 * 8. Deferred: GSAP (requestIdleCallback)
 * 9. Deferred: Three.js (desktop only, requestIdleCallback)
 */

// 1. Register Alpine plugins
Alpine.plugin(collapse);
Alpine.plugin(intersect);
Alpine.plugin(focus);

// 2. Register Alpine stores (persist across Swup page transitions)
Alpine.store('toasts', {
    items: [],
    counter: 0,

    add(message, type = 'info', duration = 5000) {
        const id = ++this.counter;
        this.items.push({ id, message, type });

        if (duration > 0) {
            setTimeout(() => this.remove(id), duration);
        }
    },

    remove(id) {
        this.items = this.items.filter((t) => t.id !== id);
    },

    success(message) {
        this.add(message, 'success');
    },

    error(message) {
        this.add(message, 'error', 0);
    },

    warning(message) {
        this.add(message, 'warning');
    },
});

// Pong easter-egg store — shared between the overlay island and the 404 page.
//
// `introHeading` / `introSubheading` / `exitLabel` / `exitHref` are optional
// overrides used by the 404 page to re-skin the overlay's intro + exit button
// without touching the overlay partial itself. Defaults restore the standard
// easter-egg copy and behaviour (Exit = close store).
Alpine.store('pong', {
    active: false,
    mode: '1p',
    introHeading: 'Controls',
    introSubheading: '',
    exitLabel: 'Exit',
    exitHref: '',
    open() {
        this.active = true;
        document.body.classList.add('overflow-hidden');
    },
    close() {
        this.active = false;
        document.body.classList.remove('overflow-hidden');
    },
    setMode(mode) {
        this.mode = mode;
    },
    setIntroCopy(heading, subheading = '') {
        this.introHeading = heading;
        this.introSubheading = subheading;
    },
    setExit(label, href = '') {
        this.exitLabel = label;
        this.exitHref = href;
    },
});

// Pong reveal bridge — listens for `pong:reveal` (dispatched by the logo
// long-press trigger) and coordinates the circular clip-path entry animation
// before opening the pong store. The overlay's `.pong-overlay--revealing`
// class scopes the keyframe so the default 404 entry remains instant.
window.addEventListener('pong:reveal', (event) => {
    const overlay = document.querySelector('.pong-overlay');
    if (!overlay) {
        Alpine.store('pong').open();
        return;
    }

    const { x, y } = event.detail || {};
    if (typeof x === 'number' && typeof y === 'number') {
        document.documentElement.style.setProperty('--pong-origin-x', `${x}px`);
        document.documentElement.style.setProperty('--pong-origin-y', `${y}px`);
    }

    overlay.classList.add('pong-overlay--revealing');
    const clear = () => overlay.classList.remove('pong-overlay--revealing');
    overlay.addEventListener('animationend', clear, { once: true });

    Alpine.store('pong').open();
});

// 3. Register Level 2 component definitions
Alpine.data('accordion', accordion);
Alpine.data('melbourneClock', melbourneClock);
Alpine.data('testimonialsCarousel', testimonialsCarousel);
Alpine.data('toastContainer', toastContainer);
Alpine.data('pong404', pong404);
Alpine.data('projectGalleryCarousel', projectGalleryCarousel);
Alpine.data('projectStoryDrawer', projectStoryDrawer);
Alpine.data('projectScrollHint', projectScrollHint);
Alpine.data('stickyHeader', stickyHeader);
Alpine.data('directorsPin', directorsPin);
Alpine.data('windFieldControls', windFieldControls);
Alpine.data('logoPongTrigger', logoPongTrigger);
Alpine.data('backLink', backLink);
Alpine.data('fitText', fitText);

// 4. Initialise Async Alpine (lazy component loading)
Alpine.plugin(AsyncAlpine);
Alpine.asyncData('contactForm', () => import('./components/contact-form.js'));
Alpine.asyncData('carousel', () => import('./components/carousel.js'));
Alpine.asyncData('pongGame', () => import('./components/pong-game.js'));

// 5. Start Alpine
Alpine.start();

// Make Alpine available globally for debugging
window.Alpine = Alpine;

// 6. Initialise Swup router
const swup = createRouter();

// Sync aria-current on main/mobile nav across Swup navigations. The nav DOM
// is persistent (outside Swup containers) so CSS transitions on the underline
// animate as aria-current toggles.
initNavActiveSync(swup);

// Re-init Alpine on Swup page navigation. ScrollTriggers are torn down in the
// same `before` step so Alpine's subsequent initTree can recreate them on the
// fresh DOM without a later kill-all wiping the new ones out.
swup.hooks.before('content:replace', () => {
    if (window.ScrollTrigger) {
        window.ScrollTrigger.getAll().forEach((st) => st.kill());
    }
    Alpine.destroyTree(document.querySelector('#swup'));
});
swup.hooks.on('content:replace', () => {
    Alpine.initTree(document.querySelector('#swup'));
});

// Scale home intro wordmark immediately (before GSAP loads)
scaleHomeIntro();

// 7. Analytics
initPageviewTracking(swup);
initEventTracking(swup);

// Defer GTM to idle — Tier 4 asset
if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
        import('./analytics/gtm.js').then(({ loadGTM }) => {
            loadGTM(import.meta.env.VITE_GTM_ID);
        });
    });
} else {
    setTimeout(() => {
        import('./analytics/gtm.js').then(({ loadGTM }) => {
            loadGTM(import.meta.env.VITE_GTM_ID);
        });
    }, 2000);
}

// ScrollTrigger refresh after the new DOM is in place. (Teardown happens in
// the `before('content:replace')` hook above, before Alpine recreates the
// components that own the new triggers.)
swup.hooks.on('page:view', () => {
    if (window.ScrollTrigger) {
        window.ScrollTrigger.refresh();
    }
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    initScrollReveals();
    scaleHomeIntro();
    initHomeIntro();
    initTagline(window.gsap, reducedMotion);
    initHeroReveal();
    initDirectorsReveal();
});

// 8. Deferred: GSAP + ScrollTrigger (loaded on first idle moment)
function loadGsap() {
    Promise.all([
        import('gsap'),
        import('gsap/ScrollTrigger'),
        import('gsap/Flip'),
        import('gsap/SplitText'),
    ]).then(([{ gsap }, { ScrollTrigger }, { Flip }, { SplitText }]) => {
        gsap.registerPlugin(ScrollTrigger, Flip, SplitText);
        window.gsap = gsap;
        window.ScrollTrigger = ScrollTrigger;
        window.Flip = Flip;
        window.SplitText = SplitText;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        initScrollReveals();
        initHomeIntro();
        initTagline(gsap, reducedMotion);
        initHeroReveal();
        initDirectorsReveal();
        // Signal Async Alpine islands (e.g. carousel) that need ScrollTrigger.
        window.dispatchEvent(new CustomEvent('gsap:ready'));
    });
}

if ('requestIdleCallback' in window) {
    requestIdleCallback(loadGsap);
} else {
    setTimeout(loadGsap, 1000);
}

// 9. Deferred: Shader system (capability-gated, not viewport-gated)
// Runs GPU probe → if capable, initialises RenderManager + bridges + demo effects.
// Safari (including iOS — and therefore every iOS browser, since Apple forces
// WebKit) does not implement requestIdleCallback, so fall back to setTimeout
// to ensure the shader system loads on iPhone/iPad.
const loadShaders = async () => {
    try {
        const { runProbe } = await import('./shaders/probe.js');
        const probeResult = await runProbe();

        if (!probeResult.capable) return;

        const { createRenderManager } = await import('./shaders/manager.js');
        const manager = createRenderManager(probeResult);

        // Init bridges (each guards against missing dependencies)
        const { initSwupBridge } = await import('./shaders/bridges/swup-bridge.js');
        initSwupBridge(manager, swup);

        const { initGsapBridge } = await import('./shaders/bridges/gsap-bridge.js');
        initGsapBridge(manager);

        // Register effects — each targets a <canvas> element in the DOM
        const { NoiseBgEffect } = await import('./shaders/effects/noise-bg.js');
        manager.register(new NoiseBgEffect(), { target: '#hero-shader' });

        const { DisplacementTransitionEffect } = await import('./shaders/effects/displacement-transition.js');
        manager.register(new DisplacementTransitionEffect(), { target: '#shader-canvas' });

        // Wind field — ambient grass background sitting behind all content.
        // Exposed on window so the Alpine controls panel can adjust params live.
        const { WindFieldEffect, PARAMS: WIND_FIELD_PARAMS, PRESETS: WIND_FIELD_PRESETS, BASE_SPEED_DEFAULT } = await import('./shaders/effects/wind-field.js');
        const windField = new WindFieldEffect();
        // pixelRatio: 1 — the shader snaps to a pixel grid anyway, so rendering at 2× DPR
        // burns ~4× fragment work for no visible gain on retina/ProMotion displays.
        manager.register(windField, { target: '#wind-field-canvas', pixelRatio: 1 });
        window.__windField = {
            effect: windField,
            params: WIND_FIELD_PARAMS,
            presets: WIND_FIELD_PRESETS,
            baseSpeedDefault: BASE_SPEED_DEFAULT,
        };
        window.dispatchEvent(new CustomEvent('wind-field:ready'));

        // Expose for dev tools
        if (import.meta.env.DEV) {
            window.__shaderManager = manager;
        }
    } catch (err) {
        if (import.meta.env.DEV) {
            console.warn('[Shaders] Initialisation failed:', err);
        }
    }
};

if ('requestIdleCallback' in window) {
    requestIdleCallback(loadShaders);
} else {
    setTimeout(loadShaders, 1000);
}
